import { Button, Form, Space } from 'antd';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useFetchData,
  useFetchFile,
  useUpdateData,
} from '../../../../../hooks';
import {
  BOM_API,
  useDeleteBOMFile,
  useUploadBOMFiles,
} from '../../../../../hooks/useBillOfMaterialHooks';
import { BillOfMaterialRecord } from '../../../../../types/BillOfMaterial';
import { FormField } from '../../../../../types';
import ErrorModal from '../../../../../components/ui/modal/ErrorModal';
import InfoModal from '../../../../../components/ui/modal/InfoModal';
import BillOfMaterialInfoTable from '../info/BillOfMaterialInfoTable';
import BillOfMaterialInfoLoading from '../info/BillOfMaterialInfoLoading';
import CustomForm from '../../../../../components/ui/CustomForm';
import SubmitButton from '../../../../../components/ui/SubmitButton';
import PageHeaderAction from '../../../../../components/PageHeaderAction';
import dayjs from 'dayjs';
import { dateFormat } from '../../../../../lib/utils';

const BillOfMaterialForm = () => {
  const params = useParams();
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [errorMess, setErrorMess] = useState('');
  const { isLoading, data, isError, error } =
    useFetchData<BillOfMaterialRecord>(`${BOM_API}/${params.id}`);
  const { data: dataFiles } = useFetchFile(
    `${BOM_API}/${params.id}/files`,
    'api/pr/requests',
  );
  const {
    mutate: mutate,
    isSuccess: isSuccessUpdate,
    isError: isErrorUpdate,
    error: errorUpadte,
  } = useUpdateData(`${BOM_API}`);
  const mutationDelete = useDeleteBOMFile();
  const { mutate: mutateFile } = useUploadBOMFiles();

  useEffect(() => {
    if (isSuccessUpdate) setIsInfoModalOpen(true);
    if (isErrorUpdate) {
      setIsErrorModalOpen(true);
      setErrorMess(errorUpadte?.message);
    }
    if (isError) {
      setIsErrorModalOpen(true);
      setErrorMess(error.message);
    }
  }, [isSuccessUpdate, isErrorUpdate]);

  const handleErrorModalClose = () => {
    setIsErrorModalOpen(false);
  };

  const handleDeleteFile = (file: any) => {
    mutationDelete.mutate(file.uid);
  };
  const onFinish = (values: BillOfMaterialRecord) => {
    mutate({
      id: params.id,
      form: {
        ...data,
        id: params.id,
        requiredDate:
          values.requiredDate === null
            ? null
            : dayjs(values.requiredDate).format(dateFormat),
        remarks: values.remarks,
      },
    });

    if (values.files !== undefined) {
      const fileList = values.files.fileList;
      const formData = new FormData();
      fileList.forEach((file: any) => {
        formData.append('files', file.originFileObj);
      });
      mutateFile({ id: params?.id, formData: formData });
    }
  };

  const handleInfoModalClose = () => {
    setIsInfoModalOpen(false);
    navigate(`/production/bill-of-material/${params.id}`);
  };

  const fileUploads = dataFiles?.map((file) => ({
    uid: file.id,
    name: file.origFilename,
    status: 'done',
    url: file.code,
  }));

  const handleCancel = () => {
    navigate(`/production/bill-of-material/${params.id}`);
  };
  const fields: FormField[] = [
    {
      name: 'code',
      label: 'BOM No',
      disabled: true,
      type: 'input',
      defaultValue: data?.code,
    },
    {
      name: 'proderno',
      label: 'PRODER no',
      disabled: true,
      type: 'input',
      defaultValue: data?.proderno,
    },
    {
      name: 'pono',
      label: 'PO no',
      disabled: true,
      type: 'input',
      defaultValue: data?.pono,
    },
    {
      name: 'requiredDate',
      label: 'Required Date',
      type: 'datepicker',
      defaultValue: data?.requiredDate,
    },
    {
      name: 'dateEntry',
      label: 'Date Entry',
      disabled: true,
      type: 'datepicker',
      defaultValue: data?.dateEntry,
    },
    {
      name: 'dateshipment',
      label: 'Shipment Date',
      disabled: true,
      type: 'datepicker',
      defaultValue: data?.dateshipment,
    },
    {
      name: 'revision',
      label: 'Revision',
      disabled: true,
      type: 'input',
      defaultValue: data?.revision,
    },
    {
      name: 'status',
      label: 'Status',
      disabled: true,
      type: 'input',
      defaultValue: data?.status,
    },
    {
      name: 'remarks',
      label: 'Remarks',
      disabled: false,
      type: 'textarea',
      defaultValue: data?.remarks,
    },
    {
      name: 'files',
      label: 'Attachments',
      disabled: false,
      type: 'file',
      defaultValue: fileUploads,
    },
  ];

  return (
    <>
      <div className={'flex items-center justify-between'}>
        <PageHeaderAction action={'Request'} moduleName={'Bill Of Material'} />
        <Space>
          <Button onClick={handleCancel}>Close</Button>
          <SubmitButton loading={false} form={form}>
            Submit
          </SubmitButton>
        </Space>
      </div>
      <div className="bg-white mt-4 h-[calc(84vh)] overflow-auto">
        {isLoading ? (
          <div className="p-4">
            <BillOfMaterialInfoLoading active={true} />
            <div className={'text-base font-semibold leading-6'}>
              Order Lines
            </div>
          </div>
        ) : (
          <div>
            <div className="p-4">
              <CustomForm
                onDeleteFile={handleDeleteFile}
                data={{ ...data, files: fileUploads }}
                formFields={fields}
                form={form}
                onFinish={onFinish}
                rowCol={3}
                layout={'vertical'}
                formName={'po_form_update'}
              />
            </div>
            <div>
              <span>Details</span>
              <BillOfMaterialInfoTable data={data?.details} />
            </div>
          </div>
        )}
      </div>

      <ErrorModal
        visible={isErrorModalOpen}
        message={errorMess}
        onClose={handleErrorModalClose}
      />

      <InfoModal
        visible={isInfoModalOpen}
        message={'BOM successfully updated'}
        onClose={handleInfoModalClose}
      />
    </>
  );
};

export default BillOfMaterialForm;
